import { type ComponentPropsWithoutRef, type Ref, useState } from "react"
import { useOutsideClick } from "../../../../hooks/useOutsideClick"
import { classNames } from "../../../messaging/utils"
import { Tooltip } from "../../atoms/Tooltip"
import styles from "./ClickableTooltip.module.scss"
import type { DivotTooltipProps } from "../DivotTooltip/DivotTooltip"

export enum Placement {
    none, // Caller will use the default or specify their own placement
    top,
    topMiddle,
    // TODO: Add more as needed
}

const TOOLTIP_EXTRA_PROPS = {
    "data-testid": "popup-tooltip",
}

const PLACEMENT_STYLE_TO_CLASSNAME: { [id in Placement]?: string } = {
    [Placement.top]: styles.top,
    [Placement.topMiddle]: styles.topMiddle,
}

interface TooltipProps extends ComponentPropsWithoutRef<"div"> {
    /**
     * Whether to click or hover to open the tooltip
     */
    clickToOpen: boolean
    /**
     * Text to display in the tooltip
     */
    text?: string
    /**
     * Additional classes to provide to the inner div
     */
    className?: string
    /**
     * Additional classes to provide to the inner div
     */
    toolTipClassName?: string
    /**
     * Props to pass to the divot tooltip component
     */
    divotProps?: DivotTooltipProps
    /**
     * Props to pass to the divot tooltip component
     */
    innerRef?: Ref<HTMLDivElement>
    /**
     * General cardinal placement of the tooltip
     */
    placement?: Placement
}
/**
 * A text tooltip that pops up when clicked and closes when clicked anywhere.
 * If `text` is falsey, the tooltip will not render.
 *
 * @example
 * ```jsx
 * const ComponentWithTooltip = () => {
 *   return (
        <ClickableTooltip
            clickToOpen={true}
            text={t`Tooltip Text.`}
            placement={Placement.top}
        >
            <div>Hello World</div>
        </ClickableTooltip>
 *   )
 * }
 * ```
 */
export const ClickableTooltip = ({
    clickToOpen,
    text,
    children,
    className,
    toolTipClassName,
    divotProps,
    innerRef,
    placement = Placement.none,
}: TooltipProps) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)
    const openToolTip = () => {
        setIsTooltipOpen(!isTooltipOpen)
    }
    const closeToolTip = () => {
        setIsTooltipOpen(false)
    }
    const inputRef = useOutsideClick(closeToolTip)

    const placementStyle = PLACEMENT_STYLE_TO_CLASSNAME[placement]

    const tooltipPopup = () => {
        if (clickToOpen) {
            return (
                isTooltipOpen && (
                    <Tooltip
                        divotProps={divotProps}
                        innerRef={innerRef}
                        className={classNames(placementStyle, toolTipClassName)}
                        text={text}
                        {...TOOLTIP_EXTRA_PROPS}
                    />
                )
            )
        } else {
            return (
                <Tooltip
                    divotProps={divotProps}
                    innerRef={innerRef}
                    className={classNames(
                        placementStyle,
                        toolTipClassName,
                        styles.tooltipDisplayer,
                    )}
                    text={text}
                    {...TOOLTIP_EXTRA_PROPS}
                />
            )
        }
    }

    return (
        <div
            className={classNames(styles.tooltipContainer, className)}
            onClick={openToolTip}
        >
            {/* In some modes the display can add extra line height and shift the children. Setting lineHeight ensures that won't happen. */}
            <div style={{ lineHeight: 0 }} ref={inputRef}>
                {children}
            </div>
            {text && tooltipPopup()}
        </div>
    )
}
